import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { readerApi, reportApi, challengeApi } from '../services/api';
import useSeason from '../hooks/useSeason';

const ReadingPulseContext = createContext(null);
export const useReadingPulse = () => useContext(ReadingPulseContext);

export function ReadingPulseProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { lastGlobalStatsUpdate, lastReadingReportCreated } = useWebSocket();
  const season = useSeason();
  const year = new Date().getFullYear();
  const [personal, setPersonal] = useState(null);
  const [community, setCommunity] = useState(null);
  const [personalState, setPersonalState] = useState('loading');
  const [communityState, setCommunityState] = useState('loading');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRevision(value => value + 1);
    window.addEventListener('reading-progress-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('reading-progress-changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    readerApi.getReaders().then(async ({ data }) => {
      const reader = data.readers?.find(item => item.isPrimary);
      if (!reader) { if (!cancelled) setPersonalState('empty'); return; }
      const response = await reportApi.getStats({ readerId: reader.id, startDate: `${year}-01-01`, endDate: `${year}-12-31` });
      if (!cancelled) { setPersonal(response.data.stats); setPersonalState('ready'); }
    }).catch(() => { if (!cancelled) setPersonalState('error'); });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, year, pathname, revision, lastReadingReportCreated]);
  useEffect(() => {
    if (!season?.id) { setCommunityState('empty'); return; }
    let cancelled = false;
    setCommunity(null);
    setCommunityState('loading');
    challengeApi.getStats(season.id).then(({ data }) => {
      if (!cancelled) { setCommunity(data.stats); setCommunityState('ready'); }
    }).catch(() => { if (!cancelled) setCommunityState('error'); });
    return () => { cancelled = true; };
  }, [season?.id, revision, lastGlobalStatsUpdate]);
  return <ReadingPulseContext.Provider value={{ personal, community, personalState, communityState, season, year }}>{children}</ReadingPulseContext.Provider>;
}
